import { CaretSortIcon, CheckIcon, UpdateIcon } from '@radix-ui/react-icons';
import { useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import api from '../../lib/api';
import { cn } from '../../lib/utils';
import { Button } from '../ui/button';
import {
    Command,
    CommandEmpty,
    CommandGroup,
    CommandInput,
    CommandItem,
    CommandList,
} from "../ui/command";
import {
    Dialog,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle
} from "../ui/dialog";
import {
    Form,
    FormControl,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
} from '../ui/form';
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from "../ui/popover";
import { toast } from '../ui/use-toast';

const ConnectCupDialog = ({ sportClass, fetchCompetition, competitionId, isDialogOpen, setIsDialogOpen }) => {
    const form = useForm()

    const [isLoading, setIsLoading] = useState(false);
    const [isFetched, setIsFetched] = useState(false);
    const [cupDialogOpen, setCupDialogOpen] = useState(false);

    const [selectedCup, setSelectedCup] = useState();
    const [cups, setCups] = useState([]);

    const fetchCups = useCallback(async () => {
        if (!isDialogOpen) return;

        try {
            const response = await api.get(`https://skhrf-api.cmdigital.se/v1/cups/competition/${competitionId}`);


            const filteredCups = response.data.filter(cup => !cup.deleted_at);
            setCups(filteredCups);
        } catch (error) {
            toast({
                variant: 'destructive',
                title: 'Något gick fel.',
                description: error?.response?.data?.error,
            });
        } finally {
            setIsFetched(true);
        }
    }, [competitionId, isDialogOpen]);

    useEffect(() => {
        fetchCups();
    }, [fetchCups]);

    const handleChange = async (e) => {
        e.preventDefault();
        setIsLoading(true);
        try {
            const response = await api.post(`https://skhrf-api.cmdigital.se/v1/cups/connect`, {
                cupId: selectedCup,
                classId: sportClass.id
            });

            fetchCompetition();
            toast({
                title: 'Klass kopplad',
                description: `Klass ${sportClass.name} kopplades.`,  // Using full class name
            });
            setIsDialogOpen(false);
        } catch (error) {

            toast({
                variant: 'destructive',
                title: 'Något gick fel.',
                description: error?.response?.data?.error,
            });
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <Dialog
            open={isDialogOpen}
            onOpenChange={(state) => {
                setIsDialogOpen(state);
            }}
        >
            <DialogContent className="flex flex-col gap-6">
                <DialogHeader className='text-left'>
                    <DialogTitle>
                        Koppla {sportClass.name} till en cup
                    </DialogTitle>
                    <DialogDescription>
                        Koppla klassen till en cup.
                    </DialogDescription>
                </DialogHeader>
                {isFetched ? (
                    <Form {...form} >
                        <form onSubmit={handleChange} className="flex flex-col gap-6">
                            <FormField
                                control={form.control}
                                name="cupId"
                                render={({ field }) => (
                                    <FormItem className='space-y-0 grid gap-2'>
                                        <FormLabel>Cup</FormLabel>
                                        <Popover open={cupDialogOpen} onOpenChange={setCupDialogOpen} modal={true}>
                                            <PopoverTrigger asChild>
                                                <FormControl>
                                                    <Button
                                                        variant="outline"
                                                        role="combobox"
                                                        className={cn(
                                                            "w-full justify-between",
                                                            !field.value && "text-muted-foreground"
                                                        )}
                                                    >
                                                        {field.value
                                                            ? cups.find(
                                                                (cup) => cup.id === field.value
                                                            )?.name
                                                            : "Välj cup"}
                                                        <CaretSortIcon className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                                                    </Button>
                                                </FormControl>
                                            </PopoverTrigger>
                                            <PopoverContent className="popover-content-width-full p-0">
                                                <Command>
                                                    <CommandInput placeholder="Sök cup..." />
                                                    <CommandList>
                                                        <CommandEmpty>Ingen cup hittades.</CommandEmpty>
                                                        <CommandGroup>
                                                            {cups.map((cup) => (
                                                                <CommandItem
                                                                    className='cursor-pointer'
                                                                    value={cup.id}
                                                                    key={cup.id}
                                                                    onSelect={() => {
                                                                        setCupDialogOpen(false);
                                                                        form.setValue("cupId", cup.id);
                                                                        setSelectedCup(cup.id);
                                                                    }}
                                                                >
                                                                    <CheckIcon
                                                                        className={cn(
                                                                            "mr-2 h-4 w-4",
                                                                            cup.id === field.value
                                                                                ? "opacity-100"
                                                                                : "opacity-0"
                                                                        )}
                                                                    />
                                                                    {cup.name}
                                                                </CommandItem>
                                                            ))}
                                                        </CommandGroup>
                                                    </CommandList>
                                                </Command>
                                            </PopoverContent>
                                        </Popover>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                            <div className='grid grid-cols-2 gap-3'>
                                <DialogClose asChild>
                                    <Button type='button' variant='secondary'>Avbryt</Button>
                                </DialogClose>
                                <Button type="submit" className="w-full" disabled={isLoading}>
                                    {isLoading ? <UpdateIcon className='loader-spinner' /> : <>Koppla klass</>}
                                </Button>
                            </div>
                        </form>
                    </Form>
                ) : <UpdateIcon className='loader-spinner self-center size-6' />}
            </DialogContent>
        </Dialog>
    );
};

export default ConnectCupDialog;
